import {
  TransferStatus,
  TransactionEventType,
  CardTransactionProcessingType as TransactionProcessingType,
  InterFinancialAccountTransferStatus,
  StatementCreditType,
  FinancialEventType,
} from '@api/mesa/graphql'
import { PurchaseIcon } from '@/src/assets/icons/PurchaseIcon'
import { Routes } from '@/src/common/enums/routes'
import ReturnIcon from '@/src/assets/icons/ReturnIcon'
import { PaymentIcon } from '@/src/assets/icons/PaymentIcon'
import type { CardActivity } from './CardActivity'
import { capitalize } from 'lodash'
import { CardActivityItem } from './CardActivityItem'
import { mesaColorNames } from '@/src/common/theme'

function useGetAmountColorFromStatus() {
  return (status: string, processingType?: string) => {
    if (status === TransactionEventType.PaymentCardAuthorizationApprovedEvent.toString()) {
      return mesaColorNames['Greyscale/500 #6E7375']
    } else if (
      (status === TransactionEventType.PaymentCardClearedEvent.toString() &&
        processingType === TransactionProcessingType.Returns) ||
      status === TransferStatus.Initiated.toString() ||
      status === TransferStatus.Pending.toString() ||
      status === TransferStatus.Processing.toString()
    ) {
      return mesaColorNames['Mesa Third #E78A43']
    } else if (status === TransferStatus.Processed.toString()) {
      return mesaColorNames['Wide/600 #00A498']
    } else {
      return mesaColorNames['Primary/Brand #1C1C1C']
    }
  }
}

export function CardActivityFeedRow({ cardActivity }: { cardActivity: CardActivity }) {
  const getAmountColorFromStatus = useGetAmountColorFromStatus()
  switch (cardActivity.__typename) {
    case 'CardActivityTransactionEvent': {
      const isPendingTransaction =
        cardActivity.transactionEventType ===
        TransactionEventType.PaymentCardAuthorizationApprovedEvent
      const isRefundTransaction =
        cardActivity.transactionProcessingType === TransactionProcessingType.Returns
      const transactionIcon = isRefundTransaction ? <ReturnIcon /> : <PurchaseIcon />
      const amountTextColor = getAmountColorFromStatus(
        cardActivity.transactionEventType,
        cardActivity.transactionProcessingType,
      )

      const totalPointsEarned =
        cardActivity.pointsEarn?.reduce((total, pointsEarn) => total + pointsEarn.points, 0) ?? 0

      return (
        <CardActivityItem
          {...(isPendingTransaction
            ? undefined
            : {
                linkProps: {
                  href: {
                    pathname: Routes.TransactionEventDetails.route,
                    params: { id: cardActivity.transactionEventId },
                  },
                },
              })}
          icon={transactionIcon}
          title={cardActivity.merchantDetails?.name ?? cardActivity.merchantDetails?.description}
          date={cardActivity.sourceCreatedAt}
          status={isPendingTransaction ? 'Pending' : undefined}
          amount={`${isRefundTransaction ? '-' : ''}${cardActivity.renderedAmount.amount}`}
          amountColor={amountTextColor}
          points={totalPointsEarned}
        />
      )
    }
    case 'CardActivityCardPayment': {
      return (
        <CardActivityItem
          icon={<PaymentIcon />}
          // @todo render `title` and `details` from `CardPayment`'s `fromHighnoteExternalAccount HighnoteExternalAccount? relation`, exactly how `ScheduledTransferRow` does
          title="Payment"
          status={capitalize(cardActivity.transferStatus)}
          amount={`-${cardActivity.amount}`}
          amountColor={getAmountColorFromStatus(cardActivity.transferStatus)}
          date={cardActivity.sourceCreatedAt}
        />
      )
    }
    case 'CardActivityStatementCredit': {
      const isPendingTransfer =
        cardActivity.highnoteFinancialAccountTransferStatus ===
        InterFinancialAccountTransferStatus.Pending
      const isPointsRedemption =
        cardActivity.statementCreditType === StatementCreditType.PointsRedemption

      const title = isPointsRedemption ? 'Statement Credit' : cardActivity.name
      const amountColor = isPendingTransfer
        ? mesaColorNames['Mesa Third #E78A43']
        : mesaColorNames['Wide/600 #00A498']

      return (
        <CardActivityItem
          icon={<PaymentIcon />}
          title={title}
          status={isPendingTransfer ? 'Pending' : undefined}
          amount={`-${cardActivity.amount}`}
          amountColor={amountColor}
          date={cardActivity.sourceCreatedAt}
        />
      )
    }
    case 'CardActivityFinancialEvent': {
      const isRefund = parseFloat(cardActivity.amount) < 0
      const transactionIcon = isRefund ? <ReturnIcon /> : <PurchaseIcon />
      const amountColor = isRefund
        ? mesaColorNames['Mesa Third #E78A43']
        : mesaColorNames['Wide/600 #00A498']
      let title = ''
      switch (cardActivity.financialEventType) {
        case FinancialEventType.CheckPaymentEvent:
          title = 'Check Payment'
          break
        case FinancialEventType.CreditCardTransferEvent:
          title = 'Interest Fees'
          break
        case FinancialEventType.FeeTransferEvent:
          title = 'Fee'
          break
        case FinancialEventType.InterFinancialAccountTransfer:
          title = 'Inter-Account Transfer'
          break
        case FinancialEventType.ManualAdjustmentEvent:
          title = 'Manual Adjustment'
          break
        case FinancialEventType.Transfer:
          title = 'Transfer'
          break
        default:
          title = 'N/A'
          break
      }
      return (
        <CardActivityItem
          icon={transactionIcon}
          title={title}
          details={cardActivity.description ?? ''}
          status={undefined}
          amount={cardActivity.amount}
          amountColor={amountColor}
          date={cardActivity.sourceCreatedAt}
        />
      )
    }
    default:
      cardActivity satisfies never
  }
}
